import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import moment from "moment-timezone";
import { FiEdit2 } from "react-icons/fi";
import { Button } from "../../common/Button";
import { localTimeCandidates, zonedRangeToInstants } from "./time";
import { timeZoneLabel } from "./timeZones";

function validateSelection(selection, timezoneName) {
  const errors = {};
  if (!moment(selection.localDate, "YYYY-MM-DD", true).isValid()) {
    errors.localDate = "Choose a valid date.";
  }
  ["startTime", "endTime"].forEach((field) => {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(selection[field] || "")) {
      errors[field] = `Enter a valid ${field === "startTime" ? "start" : "end"} time.`;
    } else if (!errors.localDate) {
      const local = `${field === "endTime" ? selection.endDate || selection.localDate : selection.localDate} ${selection[field]}`;
      if (moment.tz(local, "YYYY-MM-DD HH:mm", timezoneName).format("YYYY-MM-DD HH:mm") !== local) {
        errors[field] = "This time is skipped by daylight saving. Choose another time.";
      }
    }
  });
  if (!Object.keys(errors).length) {
    try { zonedRangeToInstants(selection, timezoneName); }
    catch (error) { errors.endTime = error.message; }
  }
  return errors;
}

export default function ScheduleSelectionModal({ selection: initialSelection, timezoneName = "UTC", onHide, onAddHours, onBlockTime }) {
  const [selection, setSelection] = useState(initialSelection);
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const editButton = useRef(null);
  const endTimeInput = useRef(null);
  const wasEditing = useRef(false);
  const editing = !!draft;

  useEffect(() => {
    if (editing) endTimeInput.current?.focus();
    else if (wasEditing.current) editButton.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  const cancelEdit = () => {
    setDraft(null);
    setErrors({});
  };
  const applyEdit = (event) => {
    event.preventDefault();
    const nextErrors = validateSelection(draft, timezoneName);
    setErrors(nextErrors);
    const firstError = Object.keys(nextErrors)[0];
    if (firstError) {
      event.currentTarget.elements.namedItem(firstError)?.focus();
      return;
    }
    setSelection(draft);
    setDraft(null);
  };

  return (
    <Modal show onHide={onHide} centered size="lg" backdrop="static" className="custom-dialog scheduling-dialog scheduling-add-modal" aria-labelledby="schedule-selection-title">
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="schedule-selection-title">Add to schedule</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="scheduling-selection-lead">
          Add hours or a non-bookable block for this selection. This does not book a patient.
        </p>
        <div className="scheduling-selection-heading">
          <h3>{editing ? "Adjust selection" : "Selected time"}</h3>
          {!editing && (
            <button ref={editButton} type="button" className="scheduling-selection-edit" onClick={() => setDraft({ ...selection })}>
              <FiEdit2 aria-hidden="true" /> Edit selection
            </button>
          )}
        </div>
        {editing ? (
          <form noValidate onSubmit={applyEdit} className="scheduling-selection-editor" aria-label="Edit selection">
            <div className="scheduling-selection-fields">
              {[
                { name: "localDate", label: "Date", type: "date" },
                { name: "endDate", label: "End date", type: "date" },
                { name: "startTime", label: "Start time", type: "time" },
                { name: "endTime", label: "End time", type: "time" },
              ].map(({ name, label, type }) => (
                <div key={name} className={`scheduling-selection-field scheduling-selection-${name}`}>
                  <label htmlFor={`selection-${name}`}>{label}</label>
                  <input
                    ref={name === "endTime" ? endTimeInput : undefined}
                    id={`selection-${name}`}
                    name={name}
                    type={type}
                    required
                    step={type === "time" ? 60 : undefined}
                    value={draft[name] || (name === "endDate" ? draft.localDate : "")}
                    aria-invalid={!!errors[name]}
                    aria-describedby={errors[name] ? `selection-${name}-error` : undefined}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraft((current) => ({ ...current, [name]: value,
                        ...(name === "localDate" && current.endDate === current.localDate ? { endDate: value } : {}) }));
                    }}
                  />
                  {errors[name] && <span id={`selection-${name}-error`} role="alert" className="scheduling-selection-error">{errors[name]}</span>}
                </div>
              ))}
            </div>
            {[{ name: "startInstant", time: "startTime", label: "Start" }, { name: "endInstant", time: "endTime", label: "End" }].map((field) => {
              const date = field.name === "endInstant" ? draft.endDate || draft.localDate : draft.localDate;
              const candidates = localTimeCandidates(date, draft[field.time], timezoneName);
              if (candidates.length < 2) return null;
              return <div key={field.name} className="scheduling-selection-field mt-2">
                <label htmlFor={`selection-${field.name}`}>{field.label} time occurs twice</label>
                <select id={`selection-${field.name}`} className="form-control"
                  value={candidates.find((candidate) => moment(candidate).isSame(draft[field.name])) || ""}
                  onChange={(event) => setDraft({ ...draft, [field.name]: event.target.value })}>
                  <option value="">Choose a time</option>
                  {candidates.map((candidate, index) => <option key={candidate} value={candidate}>
                    {index === 0 ? "First" : "Second"} {moment.tz(candidate, timezoneName).format("h:mm A [· UTC]Z")}
                  </option>)}
                </select>
              </div>;
            })}
            <p className="scheduling-selection-timezone">Times in {timeZoneLabel(timezoneName)}</p>
            <div className="scheduling-selection-edit-actions">
              <Button type="submit">Apply changes</Button>
              <Button variant="secondary" onClick={cancelEdit}>Cancel edit</Button>
            </div>
          </form>
        ) : (
          <>
            <dl className="scheduling-selection-meta">
              <div><dt>Date</dt><dd>{moment.utc(selection.localDate, "YYYY-MM-DD").format("ddd, MMM D, YYYY")}</dd></div>
              {selection.endDate && selection.endDate !== selection.localDate && <div><dt>Ends</dt><dd>{moment.utc(selection.endDate).format("ddd, MMM D, YYYY")}</dd></div>}
              <div><dt>Time</dt><dd>{moment.utc(selection.startTime, "HH:mm").format("h:mm A")} – {moment.utc(selection.endTime, "HH:mm").format("h:mm A")}</dd></div>
              <div><dt>Time zone</dt><dd>{timeZoneLabel(timezoneName)}</dd></div>
            </dl>
            <div className="scheduling-selection-actions">
              <Button onClick={() => onAddHours(selection)}>Add working hours</Button>
              <Button onClick={() => onBlockTime(selection)}>Block this time</Button>
              <Button variant="secondary" onClick={onHide}>Dismiss</Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
